import dayjs from 'dayjs';
import { apiRequest } from '../../api/client';
import type { Branch, Chain } from '../catalog/api';

const unwrap = <T>(promise: Promise<{ data: T }>): Promise<T> => promise.then((e) => e.data);

/** Ba loại nội dung của M14 — khớp `ContentType` bên backend, enum trên dây là chữ. */
export type ContentType = 'Banner' | 'Offer' | 'News';

/**
 * `status` do NGƯỜI đặt (BR-902). **Không có `Scheduled`** — "đã xuất bản nhưng chưa tới ngày"
 * suy ra được từ `status` + `isShowing`, lưu thêm là một chỗ để lệch.
 */
export type ContentStatus = 'Draft' | 'Published' | 'Archived';

export type Weekday =
  | 'Sunday'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday';

export interface ContentSchedule {
  /** `HH:mm:ss` từ `TimeOnly` của backend — cùng convention `catalog/api.ts` */
  startsAt: string;
  endsAt: string;

  /**
   * ⚠️ Server trả **đủ bảy thứ** khi bài chạy mọi ngày (mặt nạ `WeekdayMask.EveryDay` = 127),
   * chứ không trả mảng rỗng. Nên chỗ hiển thị phải rút bảy thứ về một chữ "Mọi ngày";
   * in bảy chip là đúng dữ liệu mà không ai đọc được.
   */
  daysOfWeek: Weekday[];
}

export interface Content {
  id: string;
  type: ContentType;
  /** Rỗng = mọi chuỗi */
  chainId: string | null;
  title: string;
  body: string | null;
  imageUrl: string | null;
  /** `YYYY-MM-DD` từ `DateOnly` */
  startsOn: string;
  /** Rỗng = hiện tới khi tắt tay */
  endsOn: string | null;
  sortOrder: number;
  status: ContentStatus;

  /**
   * Bốn điều kiện của BR-901, **server tính lúc đọc** theo giờ Việt Nam.
   * Không tính lại ở client: hai bản cài đặt cùng một quy tắc thì chúng sẽ lệch,
   * và bản lệch ở đây là bản người vận hành tin.
   */
  isShowing: boolean;

  /** Rỗng = toàn chuỗi */
  branchIds: string[];
  /** Rỗng = hiện cả ngày */
  schedules: ContentSchedule[];
  excludedDates: string[];
  createdAt: string;
}

export interface ContentScheduleInput {
  startsAt: string;
  endsAt: string;
  /** Rỗng = mọi ngày (`WeekdayMask.From` trả `EveryDay`) */
  daysOfWeek: Weekday[];
}

/**
 * `PUT` **thay cả bộ con** — `branchIds`, `schedules`, `excludedDates` phải gửi đầy đủ mỗi lần
 * lưu, không phải patch từng phần.
 */
export interface UpsertContentPayload {
  type: ContentType;
  chainId?: string | null;
  title: string;
  body?: string | null;
  imageUrl?: string | null;
  startsOn: string;
  endsOn?: string | null;
  sortOrder: number;
  branchIds: string[];
  schedules: ContentScheduleInput[];
  excludedDates: string[];
}

/**
 * Ba lựa chọn của ô lọc trạng thái trên `a-content`. Mặc định `notArchived`:
 * nội dung hết hạn thì **lưu trữ chứ không xóa** (PL chốt), nên không có bộ lọc thì sau vài
 * tháng danh sách toàn bài cũ và người vận hành phải cuộn qua rác để tới việc hôm nay.
 */
export type StatusFilter = 'notArchived' | 'all' | 'draftOnly';

export const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'notArchived', label: 'Chưa lưu trữ' },
  { value: 'all', label: 'Tất cả, kể cả lưu trữ' },
  { value: 'draftOnly', label: 'Chỉ bản nháp' },
];

export interface ContentQuery {
  type?: ContentType;
  status?: ContentStatus;
  includeArchived: boolean;
}

export function toQuery(statusFilter: StatusFilter, type?: ContentType): ContentQuery {
  switch (statusFilter) {
    case 'all':
      return { type, includeArchived: true };
    case 'draftOnly':
      return { type, status: 'Draft', includeArchived: false };
    case 'notArchived':
      return { type, includeArchived: false };
  }
}

export const contentKeys = {
  all: ['contents'] as const,
  list: (query: ContentQuery) => [...contentKeys.all, query] as const,
};

export const TYPE_LABELS: Record<ContentType, string> = {
  Offer: 'Ưu đãi hôm nay',
  News: 'Tin tức',
  Banner: 'Băng-rôn đầu màn',
};

export const TYPE_OPTIONS = (Object.entries(TYPE_LABELS) as [ContentType, string][]).map(
  ([value, label]) => ({ value, label }),
);

export const WEEKDAY_OPTIONS: { value: Weekday; label: string }[] = [
  { value: 'Monday', label: 'Thứ 2' },
  { value: 'Tuesday', label: 'Thứ 3' },
  { value: 'Wednesday', label: 'Thứ 4' },
  { value: 'Thursday', label: 'Thứ 5' },
  { value: 'Friday', label: 'Thứ 6' },
  { value: 'Saturday', label: 'Thứ 7' },
  { value: 'Sunday', label: 'Chủ nhật' },
];

const WEEKDAY_SHORT: Record<Weekday, string> = {
  Monday: 'T2',
  Tuesday: 'T3',
  Wednesday: 'T4',
  Thursday: 'T5',
  Friday: 'T6',
  Saturday: 'T7',
  Sunday: 'CN',
};

/** `HH:mm:ss` → `HH:mm`. Model không lưu giây, nên hiện giây chỉ là tiếng ồn. */
export function formatClock(value: string): string {
  return value.slice(0, 5);
}

export interface ScheduleLabel {
  hours: string;
  days: string;
}

export function scheduleLabel(schedule: ContentSchedule): ScheduleLabel {
  return {
    hours: `${formatClock(schedule.startsAt)}–${formatClock(schedule.endsAt)}`,
    days:
      schedule.daysOfWeek.length === 0 || schedule.daysOfWeek.length === 7
        ? 'Mọi ngày'
        : schedule.daysOfWeek.map((d) => WEEKDAY_SHORT[d]).join(', '),
  };
}

/**
 * Khoảng ngày. Prototype viết `20/08 tới 30/09`; ở đây giữ cả năm — danh sách có bài của
 * nhiều năm và bỏ năm đi thì hai đợt khác năm trông giống nhau.
 */
export function dateRangeLabel(content: Content): string {
  const from = dayjs(content.startsOn).format('DD/MM/YYYY');
  if (content.endsOn === null) return `${from} tới khi tắt`;
  return `${from} tới ${dayjs(content.endsOn).format('DD/MM/YYYY')}`;
}

/**
 * Phạm vi hiển thị là **cấu hình của bài** (BR-903), nên nhãn đọc từ `branchIds`/`chainId`
 * chứ không suy từ vai trò người đang xem.
 */
export function scopeLabel(content: Content, chains: Chain[], branches: Branch[]): string {
  if (content.branchIds.length === 0) {
    if (content.chainId === null) return 'Mọi chuỗi';
    const chain = chains.find((c) => c.id === content.chainId);
    return chain ? `Toàn chuỗi ${chain.name}` : 'Toàn chuỗi';
  }

  if (content.branchIds.length === 1) {
    const branch = branches.find((b) => b.id === content.branchIds[0]);
    return branch ? branch.name : '1 chi nhánh';
  }

  return `${content.branchIds.length} chi nhánh`;
}

export interface StatusBadge {
  label: string;
  tone: 'success' | 'warning' | 'default';
}

/**
 * Bốn nhãn của prototype từ HAI trường. `status` một mình không đủ: bài `Published` mà chưa tới
 * ngày và bài `Published` đang chạy trông y hệt nhau, mà đó đúng là câu người vận hành cần đọc.
 */
export function statusLabel(content: Content): StatusBadge {
  if (content.status === 'Draft') return { label: 'Bản nháp', tone: 'default' };
  if (content.status === 'Archived') return { label: 'Đã lưu trữ', tone: 'default' };
  if (content.isShowing) return { label: 'Đang hiện', tone: 'success' };
  return { label: 'Đã xuất bản, chưa hiện', tone: 'warning' };
}

function listQuery(query: ContentQuery): string {
  const params = new URLSearchParams();
  if (query.type) params.set('type', query.type);
  if (query.status) params.set('status', query.status);
  if (query.includeArchived) params.set('includeArchived', 'true');
  const q = params.toString();
  return q ? `?${q}` : '';
}

export const contentApi = {
  list: (query: ContentQuery) =>
    unwrap(apiRequest<Content[]>(`/admin/contents${listQuery(query)}`)),

  get: (id: string) => unwrap(apiRequest<Content>(`/admin/contents/${id}`)),

  create: (body: UpsertContentPayload) =>
    unwrap(apiRequest<Content>('/admin/contents', { method: 'POST', body })),

  update: (id: string, body: UpsertContentPayload) =>
    unwrap(apiRequest<Content>(`/admin/contents/${id}`, { method: 'PUT', body })),

  publish: (id: string) =>
    unwrap(apiRequest<Content>(`/admin/contents/${id}/publish`, { method: 'POST' })),

  // Không có `DELETE` ở backend, và đó là chủ ý: nội dung hết hạn thì lưu trữ.
  archive: (id: string) =>
    unwrap(apiRequest<Content>(`/admin/contents/${id}/archive`, { method: 'POST' })),
};
